import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Terminal, Zap, Shield, Check, ArrowRight, Globe, Rocket } from 'lucide-react';
import axios from '../Intercepter/APiClient';

function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('')
        .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

function CLILogin() {
  // Add CSS for animations
  const styles = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-10px); }
    }
    .spinner {
      animation: spin 1s linear infinite;
    }
    .pulse {
      animation: pulse 2s infinite;
    }
    .bounce {
      animation: bounce 1s infinite;
    }
    input:focus {
      outline: none;
      box-shadow: 0 0 0 2px #3b82f6;
    }
    button:hover:not(:disabled) {
      filter: brightness(1.1);
    }
    a:hover {
      color: white !important;
    }
  `;

  // Inject styles if not already present
  useEffect(() => {
    if (!document.getElementById('cli-login-styles')) {
      const style = document.createElement('style');
      style.id = 'cli-login-styles';
      style.textContent = styles;
      document.head.appendChild(style);
    }
  }, []);

  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authStep, setAuthStep] = useState('login'); // 'login', 'success', 'redirecting'
  const [countdown, setCountdown] = useState(5);

  // Move all hooks to the top before any conditional logic
  useEffect(() => {
    if (authStep === 'redirecting' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (authStep === 'redirecting' && countdown === 0) {
      window.location.href = 'https://www.npmjs.com/package/taptap-cli';
    }
  }, [authStep, countdown]);

  // Token validation after hooks
  const decoded = parseJwt(token);
  if (!decoded || decoded.uuid !== uuid) {
    return <Navigate to="/" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const res = await axios.post('/api/cli/finalize-login', { uuid, email, password });
      setAuthStep('success');
      
      // After 2 seconds, start redirecting
      setTimeout(() => {
        setAuthStep('redirecting');
      }, 2000);
      
    } catch (err) {
      alert('Authentication failed. Please check your credentials and try again.');
      setIsLoading(false);
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #581c87 35%, #0f172a 70%, #1e1b4b 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const mainGridStyle = {
    maxWidth: '80rem',
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '3rem',
    alignItems: 'center'
  };

  if (authStep === 'success') {
    return (
      <div style={containerStyle}>
        <div style={{
          maxWidth: '28rem',
          width: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '1rem',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '2rem',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{
            width: '4rem',
            height: '4rem',
            backgroundColor: '#10b981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem auto'
          }} className="pulse">
            <Check style={{ width: '2rem', height: '2rem', color: 'white' }} />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>Authentication Successful!</h2>
          <p style={{ color: '#d1d5db', marginBottom: '1.5rem' }}>Your CLI session has been authorized. You can now return to your terminal.</p>
          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '0.5rem',
            padding: '1rem',
            textAlign: 'left'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981', fontSize: '0.875rem' }}>
              <Terminal style={{ width: '1rem', height: '1rem' }} />
              <span>$ taptap --deploy</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (authStep === 'redirecting') {
    return (
      <div style={containerStyle}>
        <div style={{
          maxWidth: '28rem',
          width: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '1rem',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '2rem',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{
            width: '4rem',
            height: '4rem',
            backgroundColor: '#3b82f6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem auto'
          }}>
            <ArrowRight style={{ width: '2rem', height: '2rem', color: 'white' }} className="bounce" />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>Redirecting to NPM...</h2>
          <p style={{ color: '#d1d5db', marginBottom: '1.5rem' }}>Taking you to the TapTap CLI package page in {countdown} seconds</p>
          <div style={{
            width: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '9999px',
            height: '0.5rem'
          }}>
            <div style={{
              backgroundColor: '#3b82f6',
              height: '0.5rem',
              borderRadius: '9999px',
              transition: 'width 1s ease',
              width: `${((5 - countdown) / 5) * 100}%`
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={mainGridStyle}>
        
        {/* Left side - Branding */}
        <div style={{ color: 'white', padding: '0 1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem' }}>
            <div style={{
              width: '3rem',
              height: '3rem',
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '1rem'
            }}>
              <Zap style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
            </div>
            <div>
              <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', margin: 0, color: 'white' }}>TapTap CLI</h1>
              <p style={{ color: '#d1d5db', margin: 0, fontSize: '0.875rem' }}>Lightning-fast deployment tool</p>
            </div>
          </div>
          
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'white' }}>Authorize Your CLI Session</h2>
            <p style={{ color: '#d1d5db', lineHeight: '1.6', fontSize: '0.9rem' }}>
              Complete the authentication process to enable secure deployments from your terminal. 
              Once authorized, you'll be able to deploy your static sites instantly.
            </p>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
              <Shield style={{ width: '1.25rem', height: '1.25rem', color: '#10b981', marginRight: '0.75rem' }} />
              <span style={{ fontSize: '0.875rem', color: 'white' }}>Secure browser-based authentication</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
              <Rocket style={{ width: '1.25rem', height: '1.25rem', color: '#3b82f6', marginRight: '0.75rem' }} />
              <span style={{ fontSize: '0.875rem', color: 'white' }}>Deploy in seconds, not minutes</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
              <Globe style={{ width: '1.25rem', height: '1.25rem', color: '#8b5cf6', marginRight: '0.75rem' }} />
              <span style={{ fontSize: '0.875rem', color: 'white' }}>Production-ready URLs instantly</span>
            </div>
          </div>

          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            borderRadius: '0.5rem',
            padding: '1rem',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
              <Terminal style={{ width: '1rem', height: '1rem', color: '#10b981', marginRight: '0.5rem' }} />
              <span style={{ fontSize: '0.875rem', color: '#10b981' }}>Waiting for authentication...</span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>
              Session UUID: {uuid?.substring(0, 8)}...
            </div>
          </div>
        </div>

        {/* Right side - Login Form */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '1rem',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '2rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}>
          <form onSubmit={handleLogin}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white', marginBottom: '0.5rem' }}>Sign In</h3>
              <p style={{ color: '#d1d5db', fontSize: '0.875rem' }}>Enter your credentials to authorize the CLI</p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#d1d5db', marginBottom: '0.5rem' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '1rem',
                    transition: 'all 0.2s'
                  }}
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#d1d5db', marginBottom: '0.5rem' }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '0.5rem',
                    color: 'white',
                    fontSize: '1rem',
                    transition: 'all 0.2s'
                  }}
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                background: isLoading ? 'rgba(59, 130, 246, 0.5)' : 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                color: 'white',
                fontWeight: '600',
                padding: '0.875rem 1.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s',
                fontSize: '1rem'
              }}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '1rem',
                    height: '1rem',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%'
                  }} className="spinner"></div>
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <Shield style={{ width: '1rem', height: '1rem' }} />
                  <span>Authorize CLI Access</span>
                </>
              )}
            </button>

            <div style={{
              textAlign: 'center',
              paddingTop: '1rem',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              marginTop: '1rem'
            }}>
              <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                Don't have an account?
              </p>
              <button
                type="button"
                onClick={() => navigate('/cli-register')}
                style={{
                  color: '#60a5fa',
                  fontWeight: '500',
                  fontSize: '0.875rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'color 0.2s'
                }}
              >
                Create Account →
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.75rem',
        color: '#9ca3af',
        gap: '1rem'
      }}>
        <span>Powered by TapTap CLI v2.0.0</span>
        <span>•</span>
        <a 
          href="https://www.npmjs.com/package/taptap-cli" 
          style={{ color: '#9ca3af', textDecoration: 'none', transition: 'color 0.2s' }}
        >
          Documentation
        </a>
      </div>
    </div>
  );
}

export default CLILogin;